import { endOfMonth, endOfTomorrow, endOfWeek, endOfYear, startOfMonth, startOfTomorrow, startOfWeek, startOfYear } from "date-fns";
import { useState } from "react";
import { Button, Drawer, IconButton, Typography } from "@/components/atoms";
import { DateRangePickerProvider } from "@/components/atoms/DateRangePicker/context";
import { clsx } from "@/utils";
import { MobileDatePickerContent } from "./MobileDatePickerContent";

interface MobileDateDrawerProps {
  dates: [Date, Date];
  onDatesChange: (dates?: Date[]) => void;
  /** Block opening the drawer while a query is in flight (mirrors the desktop DateRangePicker guard). */
  disabled?: boolean;
}

  type Preset = "today" | "tomorrow" | "thisWeek" | "thisMonth" | "thisYear" | undefined;

  const presets: { key: Preset; label: string; getRange: () => [Date, Date] }[] = [
    { key: "today", label: "Today", getRange: () => [new Date(), new Date()] },
    { key: "tomorrow", label: "Tomorrow", getRange: () => [startOfTomorrow(), endOfTomorrow()] },
    { key: "thisWeek", label: "This week", getRange: () => [startOfWeek(new Date()), endOfWeek(new Date())] },
    { key: "thisMonth", label: "This month", getRange: () => [startOfMonth(new Date()), endOfMonth(new Date())] },
    { key: "thisYear", label: "This year", getRange: () => [startOfYear(new Date()), endOfYear(new Date())] },
  ];

  const buttonClass = "shrink-0 cursor-pointer rounded-2xl border border-neutral-dark-gray px-4 py-1.5 text-sm leading-snug";

export const MobileDateDrawer = ({ dates, onDatesChange, disabled = false }: MobileDateDrawerProps) => {
  const [preset, setPreset] = useState<Preset>("thisMonth");
  const [localDates, setLocalDates] = useState<Date[]>(dates);
  const [providerKey, setProviderKey] = useState(0);

  const handleDatesChange = (newDates?: Date[]) => {
    if (newDates && newDates.length > 0) {
      setLocalDates(newDates);
    }
    if (newDates && newDates.length > 1) {
      onDatesChange(newDates);
    }
  };

  return (
    <Drawer>
      <IconButton iconName="Calendar" variant="secondary" size="sm" disabled={disabled} />
      <div className="flex flex-col gap-4 px-4 pb-8 pt-2">
        <Typography variant="title">Select Date</Typography>

        {/* Presets */}
        <div className="no-scrollbar flex gap-2 overflow-x-auto">
          {presets.map((p) => (
            <button
              key={p.key}
              className={clsx(buttonClass, preset === p.key && "border-primary bg-primary text-white")}
              onClick={() => {
                setPreset(p.key);
                const range = p.getRange();
                setLocalDates(range);
                setProviderKey((k) => k + 1);
                onDatesChange(range);
              }}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Calendars — key forces remount when preset changes */}
        <DateRangePickerProvider key={providerKey} dates={localDates} onDatesChange={handleDatesChange}>
          <MobileDatePickerContent />
        </DateRangePickerProvider>

        {/* Buttons */}
        <div className="flex flex-col gap-2.5">
          <Drawer.Close asChild>
            <Button variant="primary" size="lg" className="w-full">
              Select Date
            </Button>
          </Drawer.Close>
          <Drawer.Close asChild>
            <Button variant="tertiary" size="md" className="w-full text-primary">
              Close
            </Button>
          </Drawer.Close>
        </div>
      </div>
    </Drawer>
  );
};
